import React, { useCallback, useEffect, useMemo, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Divider from "@mui/material/Divider";
import Fade from "@mui/material/Fade";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CustomBackdrop from "../CustomBackdrop";
import {
  fetchAllImpersonateUsers,
  switchToUser,
} from "../../datasource/impersonate";

const MAX_VISIBLE = 100;

function avatarTone(key) {
  const tones = [
    { bg: "#e8eef5", fg: "#3d5a80" },
    { bg: "#e8f2ec", fg: "#2f6b4f" },
    { bg: "#f0eaf4", fg: "#6b4f7a" },
    { bg: "#e6f1f2", fg: "#2d6a6e" },
    { bg: "#f5ebe8", fg: "#8a4f3d" },
    { bg: "#f3efe6", fg: "#7a6640" },
  ];
  const s = String(key || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h + s.charCodeAt(i) * (i + 1)) % tones.length;
  return tones[h];
}

function initials(row) {
  const n = row?.fullname_eng || row?.fullname_chi || row?.username || "?";
  const cleaned = String(n).replace(/[^A-Za-z\u4e00-\u9fff]/g, "");
  return (cleaned || String(n)).slice(0, 2).toUpperCase();
}

function displayName(row) {
  const eng = (row?.fullname_eng || "").trim();
  const chi = (row?.fullname_chi || "").trim();
  if (eng && chi && eng.toUpperCase() !== chi.toUpperCase()) {
    return `${eng} · ${chi}`;
  }
  return eng || chi || row?.username || "";
}

function displayMeta(row) {
  const parts = [];
  if (row?.username) parts.push(row.username);
  if (row?.employee_id && String(row.employee_id) !== String(row.username)) {
    parts.push(row.employee_id);
  }
  return parts.join(" · ");
}

function matchUser(u, q) {
  const needle = String(q || "").trim().toUpperCase();
  if (!needle) return true;
  return [u.username, u.employee_id, u.email, u.fullname_eng, u.fullname_chi]
    .filter(Boolean)
    .some((f) => String(f).toUpperCase().includes(needle));
}

const listboxSx = {
  maxHeight: 280,
  py: 1,
  px: 0.75,
  
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-thumb": {
    borderRadius: 3,
    backgroundColor: "rgba(0,0,0,0.18)",
  },
  "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
};

export default function SimulateSelectDialog({ open, onClose, onSwitched }) {
  const [inputValue, setInputValue] = useState("");
  const [allUsers, setAllUsers] = useState(null); 
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setInputValue("");
      setSelected(null);
      setError("");
      setSubmitting(false);
      
    }
  }, [open]);

  useEffect(() => {
    if (!open || allUsers !== null) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    fetchAllImpersonateUsers()
      .then((rows) => {
        if (!cancelled) {
          setAllUsers(rows);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err?.response?.data?.message || err.message || "載入用戶失敗");
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [open, allUsers]);

  const filterOptions = useCallback((options, state) => {
    const q = state.inputValue;
    const filtered = q.trim() ? options.filter((u) => matchUser(u, q)) : options;
    return filtered.slice(0, MAX_VISIBLE);
  }, []);

  const noOptionsText = useMemo(() => {
    if (loading && allUsers === null) return "載入中…";
    if (allUsers === null) return "載入中…";
    if (inputValue.trim()) return "找不到用戶";
    return "輸入關鍵字過濾，或點選下方列表";
  }, [loading, allUsers, inputValue]);

  const handleConfirm = useCallback(async () => {
    if (!selected?.username) {
      setError("請從列表中選擇用戶");
      return;
    }
    setSubmitting(true);
    setError("");
    try {
      await switchToUser(selected.username);
      setPageLoading(true);
      onSwitched?.();
      onClose?.();
      window.location.reload();
    } catch (err) {
      
      const apiMsg = err?.response?.data?.message;
      const raw = apiMsg || err.message || "切換失敗，請稍後再試";
      setError(
        !apiMsg && /network error/i.test(String(raw))
          ? "無法連線至伺服器（Network Error）。請確認 API 仍在執行後再試。"
          : raw,
      );
      setSubmitting(false);
      setPageLoading(false);
    }
  }, [selected, onClose, onSwitched]);

  const tone = avatarTone(selected?.username);

  return (
    <>
      {}
      {submitting || pageLoading ? (
        <CustomBackdrop text="正在切換身分…" />
      ) : null}
      <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      TransitionComponent={Fade}
      transitionDuration={220}
      PaperProps={{
        elevation: 8,
        sx: {
          borderRadius: 2,
          overflow: "hidden",
        },
      }}
    >
      {}
      <Box
        sx={{
          px: 3,
          pt: 2.5,
          pb: 2,
          display: "flex",
          alignItems: "flex-start",
          gap: 1.5,
          background: (t) =>
            `linear-gradient(135deg, ${t.palette.primary.main}14 0%, ${t.palette.primary.light}08 100%)`,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "primary.main",
            color: "primary.contrastText",
            flexShrink: 0,
          }}
        >
          <SwapHorizIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, lineHeight: 1.3, fontSize: "1.125rem" }}>
            模擬登錄
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, lineHeight: 1.45 }}>
            以選定用戶身分瀏覽與操作（僅測試環境）。憑證仍為你本人，可隨時返回。
          </Typography>
        </Box>
        <IconButton
          aria-label="關閉"
          onClick={onClose}
          disabled={submitting}
          size="small"
          sx={{ mt: -0.5, mr: -1 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}

        <Autocomplete
          options={allUsers || []}
          loading={loading && allUsers === null}
          value={selected}
          onChange={(_, val) => setSelected(val)}
          inputValue={inputValue}
          onInputChange={(_, val) => setInputValue(val)}
          getOptionLabel={(o) =>
            o
              ? `${o.fullname_eng || o.fullname_chi || ""} (${o.username})`.trim()
              : ""
          }
          isOptionEqualToValue={(a, b) => a?.username === b?.username}
          filterOptions={filterOptions}
          noOptionsText={noOptionsText}
          
          componentsProps={{
            paper: {
              elevation: 0,
              sx: {
                mt: 1,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                boxShadow: "0 8px 28px rgba(15, 23, 42, 0.12)",
                overflow: "hidden",
              },
            },
          }}
          ListboxProps={{ sx: listboxSx }}
          renderOption={(props, option, { selected: isSelected }) => {
            const { key, ...rest } = props;
            const c = avatarTone(option.username);
            return (
              <Box
                component="li"
                key={option.username || key}
                {...rest}
                sx={{
                  display: "flex !important",
                  alignItems: "center",
                  gap: 1.75,
                  mx: 0.5,
                  my: 0.25,
                  px: "12px !important",
                  py: "10px !important",
                  borderRadius: "10px !important",
                  
                  bgcolor: isSelected ? "action.selected" : "transparent",
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                  '&[aria-selected="true"]': {
                    bgcolor: "action.selected",
                  },
                  "&.Mui-focused": {
                    bgcolor: "action.hover",
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    fontSize: 12,
                    fontWeight: 600,
                    letterSpacing: 0.2,
                    bgcolor: c.bg,
                    color: c.fg,
                    flexShrink: 0,
                  }}
                >
                  {initials(option)}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      fontWeight: 600,
                      color: "text.primary",
                      lineHeight: 1.35,
                      letterSpacing: 0.01,
                    }}
                  >
                    {displayName(option)}
                  </Typography>
                  <Typography
                    variant="caption"
                    noWrap
                    component="div"
                    sx={{
                      mt: 0.25,
                      color: "text.secondary",
                      lineHeight: 1.35,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {displayMeta(option)}
                  </Typography>
                </Box>
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="搜尋姓名或帳號…"
              autoFocus
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: "text.disabled" }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {loading && allUsers === null ? (
                      <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  bgcolor: "grey.50",
                  transition: "background-color 0.15s, box-shadow 0.15s",
                  "& fieldset": {
                    borderColor: "divider",
                  },
                  "&:hover": {
                    bgcolor: "background.paper",
                    "& fieldset": { borderColor: "grey.400" },
                  },
                  "&.Mui-focused": {
                    bgcolor: "background.paper",
                    boxShadow: (t) => `0 0 0 3px ${t.palette.primary.main}22`,
                    "& fieldset": {
                      borderWidth: 1,
                      borderColor: "primary.main",
                    },
                  },
                },
              }}
            />
          )}
        />

        {}
        {selected ? (
          <Fade in>
            <Box
              sx={{
                mt: 2.5,
                p: 2,
                display: "flex",
                alignItems: "center",
                gap: 1.75,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "primary.light",
                bgcolor: (t) => `${t.palette.primary.main}0A`,
              }}
            >
              <Avatar
                sx={{
                  width: 48,
                  height: 48,
                  fontWeight: 700,
                  bgcolor: tone.bg,
                  color: tone.fg,
                }}
              >
                {initials(selected)}
              </Avatar>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" color="primary" fontWeight={600}>
                  將切換為
                </Typography>
                <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ lineHeight: 1.3 }}>
                  {displayName(selected)}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap component="div">
                  {displayMeta(selected)}
                </Typography>
              </Box>
              <CheckCircleOutlineIcon color="primary" sx={{ opacity: 0.85 }} />
            </Box>
          </Fade>
        ) : (
          <Box
            sx={{
              mt: 2.5,
              py: 2.5,
              px: 2,
              textAlign: "center",
              borderRadius: 2,
              border: "1px dashed",
              borderColor: "divider",
              bgcolor: "action.hover",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {loading && allUsers === null
                ? "正在載入 Prod 用戶列表…"
                : "從上方搜尋並點選一位用戶"}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={submitting}
          sx={{ textTransform: "none", minWidth: 88 }}
        >
          取消
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!selected || submitting}
          startIcon={
            submitting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SwapHorizIcon />
            )
          }
          sx={{
            textTransform: "none",
            px: 2.5,
            borderRadius: 1.5,
            boxShadow: "none",
            "&:hover": { boxShadow: "none" },
          }}
        >
          {submitting ? "同步用戶資料中…" : "切換為此用戶"}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}
